import React, { useEffect, useRef, useState } from "react";

import { Button } from "equator-design-system";
import { twMerge } from "tailwind-merge";

export type TabItem = {
  value: string;
  label: string;
  icon?: React.ReactNode;
  disabled?: boolean;
};

type Props = {
  tabList: TabItem[];
  selected?: string;
  className?: string;
  buttonClassName?: string;
  buttonSelectedClassName?: string;
  wrapperListClassName?: string;
  variant?: "default" | "underline";
  onSelect: (value: string) => void;
};

const TabSelect = ({
  tabList,
  selected,
  className,
  buttonClassName,
  buttonSelectedClassName,
  wrapperListClassName,
  variant = "default",
  onSelect
}: Props) => {
  const [indicatorStyle, setIndicatorStyle] = useState({ left: 0, width: 0 });
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (variant === "underline" && selected) {
      const selectedIndex = tabList.findIndex(
        (item) => item.value === selected
      );
      const selectedTab = tabRefs.current[selectedIndex];
      if (selectedTab) {
        setIndicatorStyle({
          left: selectedTab.offsetLeft,
          width: selectedTab.offsetWidth
        });
      }
    }
  }, [selected, variant, tabList]);

  return (
    <div
      ref={wrapperRef}
      className={twMerge(
        variant === "default"
          ? "flex h-[38px] bg-whisper rounded-md items-center px-1 mt-1"
          : "flex relative",
        className
      )}
    >
      {variant === "underline" && (
        <div
          className="absolute bottom-0 left-0 h-[1px] bg-gray-200"
          style={{ minWidth: wrapperRef.current?.scrollWidth || "100%" }}
        />
      )}
      <div className={twMerge("relative inline-flex", wrapperListClassName)}>
        {tabList.map((item, index) => (
          <Button
            variant="ghost"
            type="button"
            key={item.value}
            aria-label={item.icon ? item.value : ""}
            ref={(el) => (tabRefs.current[index] = el)}
            className={twMerge(
              "font-sans text-sm relative",
              variant === "default"
                ? "w-1/2 h-[30px] rounded-md"
                : "px-4 py-2 rounded-none transition-colors duration-300",
              buttonClassName,
              buttonClassName,
              variant === "default" &&
                selected === item.value &&
                "bg-white text-teal-500 font-semibold",
              variant === "default" && selected !== item.value && "",
              variant === "underline" &&
                selected === item.value &&
                "text-teal-500 font-semibold",
              variant === "underline" &&
                selected !== item.value &&
                "text-gray-500 hover:text-gray-700",
              selected === item.value && buttonSelectedClassName,
              item.disabled && "!text-light-gray cursor-not-allowed"
            )}
            disabled={item.disabled}
            onClick={() => onSelect(item.value)}
          >
            {item.icon &&
              React.cloneElement(item.icon as React.ReactElement, {
                className: twMerge(
                  (item.icon as React.ReactElement).props.className,
                  selected === item.value ? "fill-teal-500" : "fill-dim-gray"
                )
              })}
            <span>{item.label}</span>
          </Button>
        ))}
        {variant === "underline" && selected && (
          <div
            className="absolute bottom-0 h-0.5 bg-teal-500 transition-all duration-300 ease-in-out"
            style={{
              left: `${indicatorStyle.left}px`,
              width: `${indicatorStyle.width}px`
            }}
          />
        )}
      </div>
    </div>
  );
};

export default TabSelect;
